import { useQuery } from '@tanstack/react-query';
import { ASSET_TEXT_LIMITS } from '@wodi/domain';
import type { AssetView, UpdateAssetRequest } from '@wodi/shared';
import {
  Button,
  DateField,
  Dialog,
  DialogActions,
  MoneyField,
  Notice,
  Select,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { assetOptionsQuery } from '../api/assets.ts';
import { errorText } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';

// Registering an asset, or changing its details (docs/plans/phase3-operations-modules.md
// §3.3). The tag is set once; the cost is integer cents in KES.

const L = ASSET_TEXT_LIMITS;
const NO_SUPPLIER = 'none';

export function AssetFormDialog({
  title,
  confirmLabel,
  asset,
  isPending,
  error,
  onSubmit,
  onClose,
}: {
  title: string;
  confirmLabel: string;
  /** Changing an existing asset: its details, and no tag field. */
  asset?: AssetView;
  isPending: boolean;
  error: unknown;
  onSubmit: (body: UpdateAssetRequest & { tag: string }) => void;
  onClose: () => void;
}) {
  const options = useQuery(assetOptionsQuery);
  const [tag, setTag] = useState(asset?.tag ?? '');
  const [name, setName] = useState(asset?.name ?? '');
  const [category, setCategory] = useState<string | null>(asset?.category.code ?? null);
  const [makeModel, setMakeModel] = useState(asset?.makeModel ?? '');
  const [serial, setSerial] = useState(asset?.serialNumber ?? '');
  const [location, setLocation] = useState(asset?.location ?? '');
  const [department, setDepartment] = useState(asset?.department ?? '');
  const [supplier, setSupplier] = useState(asset?.supplier?.id ?? NO_SUPPLIER);
  const [purchaseDate, setPurchaseDate] = useState<string | null>(asset?.purchaseDate ?? null);
  const [cost, setCost] = useState<bigint | null>(
    asset?.purchaseCostCents == null ? null : BigInt(asset.purchaseCostCents),
  );
  const [warranty, setWarranty] = useState<string | null>(asset?.warrantyUntil ?? null);
  const [interval, setInterval] = useState(
    asset?.serviceIntervalDays == null ? '' : String(asset.serviceIntervalDays),
  );
  const [tried, setTried] = useState(false);
  const intervalValue = interval.trim() === '' ? null : Number(interval.trim());
  const intervalBad =
    intervalValue !== null &&
    (!Number.isInteger(intervalValue) || intervalValue < 1 || intervalValue > 3650);
  const problems = {
    tag: asset === undefined && tag.trim() === '' ? 'Give the tag on the asset.' : null,
    name: name.trim() === '' ? 'Name the asset.' : null,
    category: category === null ? 'Choose a category.' : null,
    interval: intervalBad ? 'Days between services, from 1 to 3650, or leave it empty.' : null,
  };
  const show = (problem: string | null) =>
    tried && problem !== null ? { errorMessage: problem } : {};
  const text = (v: string) => (v.trim() === '' ? null : v.trim());
  return (
    <Dialog
      title={title}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <div className={styles.fields}>
          {asset === undefined ? (
            <TextField
              label="Tag"
              description="As on the label, like DMC-INF-003."
              value={tag}
              onChange={setTag}
              maxLength={30}
              {...show(problems.tag)}
            />
          ) : null}
          <TextField
            label="Name"
            value={name}
            onChange={setName}
            maxLength={L.name}
            {...show(problems.name)}
          />
          <Select
            label="Category"
            placeholder="Choose one"
            options={(options.data?.categories ?? [])
              .filter((c) => c.active || c.code === asset?.category.code)
              .map((c) => ({ id: c.code, label: c.display }))}
            value={category}
            onChange={(key) => {
              setCategory(key === null ? null : String(key));
            }}
            {...show(problems.category)}
          />
          <TextField
            label="Make and model (optional)"
            value={makeModel}
            onChange={setMakeModel}
            maxLength={L.makeModel}
          />
          <TextField
            label="Serial number (optional)"
            value={serial}
            onChange={setSerial}
            maxLength={L.serial}
          />
          <TextField
            label="Location (optional)"
            value={location}
            onChange={setLocation}
            maxLength={L.location}
          />
          <TextField
            label="Department (optional)"
            value={department}
            onChange={setDepartment}
            maxLength={L.department}
          />
          <Select
            label="Supplier"
            options={[
              { id: NO_SUPPLIER, label: 'Not recorded' },
              ...(options.data?.suppliers ?? []).map((s) => ({ id: s.id, label: s.name })),
            ]}
            value={supplier}
            onChange={(key) => {
              setSupplier(key === null ? NO_SUPPLIER : String(key));
            }}
          />
          <DateField
            label="Purchase date (optional)"
            value={purchaseDate}
            onChange={setPurchaseDate}
          />
          <MoneyField
            label="Purchase cost (optional)"
            value={cost}
            onChange={(cents) => {
              setCost(cents);
            }}
          />
          <DateField label="Warranty until (optional)" value={warranty} onChange={setWarranty} />
          <TextField
            label="Days between planned services (optional)"
            value={interval}
            onChange={setInterval}
            inputMode="numeric"
            {...show(problems.interval)}
          />
        </div>
        {error === null || error === undefined ? null : (
          <Notice tone="error">{errorText(error, "It wasn't saved. Try again.")}</Notice>
        )}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Go back
        </Button>
        <Button
          variant="primary"
          isPending={isPending}
          pendingLabel="Saving…"
          onPress={() => {
            setTried(true);
            if (Object.values(problems).some((p) => p !== null)) return;
            onSubmit({
              tag: tag.trim(),
              name: name.trim(),
              categoryCode: category ?? '',
              makeModel: text(makeModel),
              serialNumber: text(serial),
              location: text(location),
              department: text(department),
              supplierId: supplier === NO_SUPPLIER ? null : supplier,
              purchaseDate,
              purchaseCostCents: cost === null ? null : cost.toString(),
              warrantyUntil: warranty,
              serviceIntervalDays: intervalValue,
            });
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
